import { useState } from "react";
import axios from "axios";
import { Loader2, KeyRound, AlertCircle, CheckCircle2, Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = { bg: "var(--bc-bg, #020202)", surface: "var(--bc-panel, #09090B)", highlight: "var(--bc-panel-elev, #141416)", border: "var(--bc-border, rgba(255,255,255,0.07))", text: "var(--bc-ink, #FFFFFF)", muted: "var(--bc-sub, #71717A)", green: "#22C55E", red: "#EF4444" };

function getOrCreateDeviceId() {
  let id = localStorage.getItem("bc_device_id");
  if (!id) {
    id = crypto.randomUUID?.() || ("dev-" + Math.random().toString(36).slice(2) + Date.now().toString(36));
    localStorage.setItem("bc_device_id", id);
  }
  return id;
}

export default function LoginPage({ onLogin, kickedReason }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setLoading(true);
    setError("");
    try {
      const device_id = getOrCreateDeviceId();
      await axios.post(`${API}/auth/validate`, { code: trimmed, device_id });
      setSuccess(true);
      setTimeout(() => onLogin({ type: "user", code: trimmed, device_id }), 600);
    } catch (err) {
      const msg = err.response?.data?.detail || "Codice non valido";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e) => {
    let v = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "");
    // Auto-format BCRK-XXXX-XXXX
    const raw = v.replace(/-/g, "");
    if (raw.length <= 4) v = raw;
    else if (raw.length <= 8) v = raw.slice(0, 4) + "-" + raw.slice(4);
    else v = raw.slice(0, 4) + "-" + raw.slice(4, 8) + "-" + raw.slice(8, 12);
    setCode(v);
    setError("");
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "1rem" }}>

      {/* Subtle grid overlay */}
      <div style={{ position: "fixed", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.015) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.015) 1px,transparent 1px)", backgroundSize: "40px 40px", pointerEvents: "none" }} />

      <div style={{ position: "relative", width: "100%", maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", marginBottom: "0.75rem" }}>
            <Activity size={18} style={{ color: C.green }} />
            <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900, fontSize: "1.6rem", letterSpacing: "0.1em", color: C.text, textTransform: "uppercase" }}>
              BET <span style={{ color: C.green }}>CRACKER</span>
            </span>
          </div>
          <p style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem", color: C.muted, letterSpacing: "0.2em", textTransform: "uppercase" }}>
            Analisi Calcistica Intelligente
          </p>
        </div>

        {/* Card */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, padding: "2rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.5rem" }}>
            <KeyRound size={14} style={{ color: C.green }} />
            <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.15em", textTransform: "uppercase", color: C.text }}>
              Accesso Riservato
            </span>
          </div>

          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.8rem", color: C.muted, marginBottom: "1.5rem", lineHeight: 1.6 }}>
            Inserisci il tuo codice di accesso personale per continuare.
          </p>

          {/* Kick banner — explains why the user was logged out */}
          {kickedReason && (
            <div data-testid="kick-banner"
              style={{
                marginBottom: "1.25rem",
                padding: "0.85rem 1rem",
                background: kickedReason === "expired" ? "rgba(245,158,11,0.08)" : "rgba(239,68,68,0.08)",
                border: `1px solid ${kickedReason === "expired" ? "rgba(245,158,11,0.35)" : "rgba(239,68,68,0.35)"}`,
                color: kickedReason === "expired" ? "#F59E0B" : "#EF4444",
                fontFamily: "'DM Sans',sans-serif",
                fontSize: "0.78rem",
                lineHeight: 1.5,
              }}>
              <strong style={{ display: "block", marginBottom: "0.2rem", letterSpacing: "0.05em", fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, textTransform: "uppercase", fontSize: "0.85rem" }}>
                {kickedReason === "expired" ? "Sessione scaduta" : "Sessione interrotta"}
              </strong>
              {kickedReason === "expired"
                ? "Il tuo codice di accesso è scaduto. Contatta l'amministratore per riceverne uno nuovo."
                : "Il codice è stato utilizzato su un altro dispositivo o revocato. Reinserisci il codice per continuare."}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "1rem" }}>
              <input
                data-testid="access-code-input"
                type="text"
                value={code}
                onChange={handleInput}
                placeholder="BCRK-XXXX-XXXX"
                maxLength={14}
                autoFocus
                style={{
                  width: "100%", boxSizing: "border-box",
                  background: C.highlight, border: `1px solid ${error ? C.red : success ? C.green : C.border}`,
                  color: C.text, padding: "0.75rem 1rem",
                  fontFamily: "'JetBrains Mono',monospace", fontSize: "1rem",
                  letterSpacing: "0.12em", textAlign: "center", outline: "none",
                  textTransform: "uppercase", transition: "border-color 0.15s",
                }}
                onFocus={(e) => { if (!error && !success) e.target.style.borderColor = "rgba(34,197,94,0.5)"; }}
                onBlur={(e) => { if (!error && !success) e.target.style.borderColor = C.border; }}
              />
            </div>

            {error && (
              <div data-testid="login-error" style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem", padding: "0.6rem 0.75rem", background: "rgba(239,68,68,0.08)", border: `1px solid rgba(239,68,68,0.2)` }}>
                <AlertCircle size={12} style={{ color: C.red, flexShrink: 0 }} />
                <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", color: C.red }}>{error}</span>
              </div>
            )}

            {success && (
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem", padding: "0.6rem 0.75rem", background: "rgba(34,197,94,0.08)", border: `1px solid rgba(34,197,94,0.2)` }}>
                <CheckCircle2 size={12} style={{ color: C.green }} />
                <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", color: C.green }}>Accesso confermato</span>
              </div>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading || success || code.length < 12}
              style={{
                width: "100%", padding: "0.75rem",
                background: success ? "rgba(34,197,94,0.15)" : (loading || code.length < 12) ? "rgba(34,197,94,0.1)" : C.green,
                color: success ? C.green : (loading || code.length < 12) ? "rgba(34,197,94,0.4)" : "#020202",
                border: `1px solid ${success ? "rgba(34,197,94,0.4)" : C.green}`,
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                fontSize: "0.85rem", letterSpacing: "0.15em", textTransform: "uppercase",
                cursor: loading || success || code.length < 12 ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
                transition: "all 0.15s",
              }}
            >
              {loading ? <Loader2 size={14} style={{ animation: "spin 0.8s linear infinite" }} /> : null}
              {loading ? "Verifica in corso..." : success ? "Accesso confermato" : "Accedi"}
            </button>
          </form>
        </div>

        <p style={{ textAlign: "center", marginTop: "1.5rem", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem", color: "#3F3F46", letterSpacing: "0.1em" }}>
          ACCESSO RISERVATO · SOLO UTENTI AUTORIZZATI
        </p>

        <p style={{ textAlign: "center", marginTop: "0.75rem", fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", color: "#71717A" }}>
          Non hai un codice?{" "}
          <a href="/auth?mode=register" data-testid="link-to-auth" style={{ color: "#C8FF00", textDecoration: "none", fontWeight: 600 }}>
            Crea un account
          </a>
          {" "}o{" "}
          <a href="/auth?mode=login" style={{ color: "#C8FF00", textDecoration: "none", fontWeight: 600 }}>
            Accedi con email
          </a>
        </p>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
